<script lang="ts">
export default {
  name: 'KkHashtagContainer',
}
</script>
<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { KkIcon } from 'src/biz_spa/components/base'
import { KkTargetStoresModal } from 'src/biz_spa/components/projects'
import { KkAccountInfoLabel } from 'src/biz_spa/components/domains/sns'
import type { Store } from 'src/biz_spa/types'

/**
 * 共通
 */
const { t } = useI18n()
const route = useRoute()

/**
 * Props
 */
type HashTag = {
  id: number
  content: string
}
type Account = {
  id: number
  username: string
  picture: {
    url: string
  }
  name: string
}
type Props = {
  setting: {
    id: number
    businessAccount: Omit<Account, 'stores' | 'hashTags'>
    hashTags: HashTag[]
    stores: Store[]
  }
}
const props = defineProps<Props>()

/**
 * Emits
 */
type Emits = {
  (event: 'delete', id: number): void
}
const emits = defineEmits<Emits>()

/**
 * Ref, Computed
 */
const showStoreModal = ref(false)
const currentCompanyUuid = computed<string>(() => route.params.company_uuid as string)
const storeNames = computed<string[]>(() => props.setting.stores.map((v: Store) => v.name))
</script>

<template>
  <tr class="hashtagContainer">
    <td>
      <div class="hashtagContainer__account">
        <KkAccountInfoLabel
          :image-url="props.setting.businessAccount.picture.url"
          sns-type="instagram"
          :name="props.setting.businessAccount.name"
          :page-id="props.setting.businessAccount.username"
        />
      </div>
    </td>
    <td class="hashtagContainer__hashtag">
      <ul v-if="props.setting.hashTags.length">
        <li v-for="( tag, i ) in props.setting.hashTags" :key="i" class="tag">
          {{ tag.content }}
        </li>
      </ul>
      <div v-else>
        {{ t('pages.companies.instagramHashtag.list.table.row.noTags') }}
      </div>
    </td>
    <td class="hashtagContainer__stores">
      <button v-if="props.setting.stores.length > 0" class="storeCount" @click="showStoreModal = true">
        <span>{{ t('labels.storesCount', props.setting.stores.length) }}</span>
      </button>
      <template v-else>
        {{ t('pages.companies.instagramHashtag.list.table.row.noStores') }}
      </template>
      <KkTargetStoresModal
        :is-visible="showStoreModal"
        :store-names="storeNames"
        @close="showStoreModal=false"
      />
    </td>
    <td class="hashtagContainer__action">
      <div class="flexContainer">
        <router-link
          class="action"
          :to="{
            name: 'CompaniesSnsInstagramHashtagEdit',
            params: { company_uuid: currentCompanyUuid, instagram_hash_tag_settings_id: props.setting.id }
          }"
        >
          <KkIcon name="edit" color="rgba(58, 59, 63, 0.7)" class="action__icon" />
          {{ t('labels.edit') }}
        </router-link>
        <button class="action" @click="emits('delete', props.setting.id)">
          <KkIcon name="delete" color="rgba(58, 59, 63, 0.7)" class="action__icon" />
          {{ t('labels.delete') }}
        </button>
      </div>
    </td>
  </tr>
</template>

<style lang="scss" scoped>
@import '../../../../../../../stylesheets/biz_spa/foundation/var';
.hashtagContainer {
  border-radius: 4px;
  border-bottom: 1px solid #E4E7EB;
  td {
    vertical-align: middle;
    &:not(:first-child) {
      border-left: none;
    }
  }
  &__account {
    display: flex;
  }
  &__hashtag {
    font-size: 12px;
  }
  &__stores {
    font-size: 12px;
  }
  &__action {
    font-size: 12px;
  }
}
.action {
  display: flex;
  text-decoration: none;
  cursor: pointer;
  &__icon {
    width: 16px;
    margin-right: 5px;
  }
}
.tag {
  display: inline-block;
	margin: 0 .5em 1em 0;
	padding: .2em .6em;
  line-height: 16px;
	color: #3A3B3F;
	background-color: #F5F6F9;
	border-radius: 2em;
}
.storeCount {
  > span {
    color: #E9446A;
    text-decoration: underline;
  }
}
.flexContainer {
  display: flex;
  gap: 16px;
  line-height: normal;
}
</style>
